import styles from "./RequiredLessonMaterials.module.scss";
import Modal from "src/components/modal/Modal";
import Button from "src/components/button/Button";
import { LordIcon } from "@src/components/lord-icon/lord-icon";
import { IconInfo, IconProjRequired, IconRateLesson } from "@src/constants";
import { SllLesson, SllModule } from "@src/types";
import MaterialCard from "@src/components/materialCard/SllCard";
import { useCallback, useMemo } from "react";
import { hasArrayValues } from "@src/utils/helpers";
import { Tooltip } from "antd";
import CheatSheetSection from "./CheatSheetSection";
import { shouldShowLessonMaterialsCheatSheet } from "./lessonMaterialsCheatSheet";

type Props = {
  open: boolean;
  lesson?: SllLesson;
  onClose: () => void;
  onStartLesson: () => void;
  onCompleteLesson: () => void;
  openFeedback: (e: any) => void;
  completedPercent?: number;
  startLabel?: string;
  hasLessonPresentationContents?: boolean;
  showLessonMaterialsNotice?: boolean;
  isEducatorCaregiverLessonLayout?: boolean | null;
  handleDownloadMaterial?: (
    content: SllModule,
    lesson?: SllLesson,
    needPrint?: boolean,
  ) => void;
};

function RequiredLessonMaterials(props: Props) {
  const {
    open,
    lesson,
    startLabel,
    showLessonMaterialsNotice = false,
    hasLessonPresentationContents = false,
    completedPercent,
    onClose,
    onStartLesson,
    onCompleteLesson,
    openFeedback,
    handleDownloadMaterial,
    isEducatorCaregiverLessonLayout,
  } = props;

  const requiredMaterials = useMemo(() => {
    if (!lesson?.materials) return [];
    return lesson?.materials.filter(
      (material) => material?.isRequired === true,
    );
  }, [lesson?.materials]);

  const optionalMaterials = useMemo(() => {
    if (!lesson?.materials) return [];
    return lesson?.materials.filter(
      (material) => material?.isRequired === false,
    );
  }, [lesson?.materials]);

  const handleDownloadLessonMaterial = useCallback(
    (content: SllModule) => {
      handleDownloadMaterial?.(content, lesson);
    },
    [lesson, handleDownloadMaterial],
  );

  const handlePrintMaterial = useCallback(
    (content: SllModule) => {
      handleDownloadMaterial?.(content, lesson, true);
    },
    [lesson, handleDownloadMaterial],
  );

  const showCheatSheet = shouldShowLessonMaterialsCheatSheet(
    isEducatorCaregiverLessonLayout ?? lesson?.isEducatorCaregiverLessonLayout,
    hasArrayValues(requiredMaterials),
  );

  return (
    <Modal
      global={false}
      open={open}
      onClose={onClose}
      closeButton={false}
      className={styles.wrapper}
    >
      <div className={styles.mainView}>
        <div className={styles.header}>
          <div className={styles.title}>{"Lesson Materials"}</div>
        </div>
        <div className={styles.content}>
          {showLessonMaterialsNotice && (
            <div className={styles.materialNote}>
              <LordIcon
                src={IconProjRequired}
                isHover={true}
                colors={{
                  primary: "#AF77B8",
                  secondary: "#AF77B8",
                }}
                stroke={5}
                size={18}
              />
              <div className={styles.materialNoteText}>
                This lesson <span>only</span> contains downloadable materials.
              </div>
            </div>
          )}
          <div className={styles.subView}>
            <div style={{ display: "flex", gap: "20px" }}>
              {hasArrayValues(requiredMaterials) && (
                <div className={styles.materialContents}>
                  <div className={styles.requireTitle}>
                    Required
                    <Tooltip
                      placement="topRight"
                      title={
                        <div className={styles.lessonTip}>
                          Required materials are essential to achieving the
                          lesson objectives.
                        </div>
                      }
                      arrowPointAtCenter
                      overlayStyle={{ maxWidth: "329px" }}
                      color="#FFFFFF"
                    >
                      <div>
                        <LordIcon
                          src={IconInfo}
                          isHover={true}
                          colors={{ primary: "#FFB073", secondary: "#FFB073" }}
                          size={20}
                        />
                      </div>
                    </Tooltip>
                  </div>
                  <div className={styles.unitsDiv}>
                    {requiredMaterials.map((material, index) => {
                      return (
                        <MaterialCard
                          key={index}
                          content={material as SllModule}
                          handleDownloadMaterial={handleDownloadLessonMaterial}
                          handlePrintMaterial={handlePrintMaterial}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
              {hasArrayValues(optionalMaterials) && (
                <div className={styles.materialContents}>
                  <div className={styles.optionTitle}>
                    Optional
                    <Tooltip
                      placement="topRight"
                      title={
                        <div className={styles.lessonTip}>
                          Optional materials are supplementary resources
                          designed to enhance learning & foster a deeper
                          understanding.
                        </div>
                      }
                      arrowPointAtCenter
                      overlayStyle={{ maxWidth: "529px" }}
                      color="#FFFFFF"
                    >
                      <div>
                        <LordIcon
                          src={IconInfo}
                          isHover={true}
                          colors={{ primary: "#6C969A", secondary: "#6C969A" }}
                          size={20}
                        />
                      </div>
                    </Tooltip>
                  </div>
                  <div className={styles.unitsDiv}>
                    {optionalMaterials.map((material, index) => {
                      return (
                        <MaterialCard
                          key={index}
                          content={material as SllModule}
                          handleDownloadMaterial={handleDownloadLessonMaterial}
                          handlePrintMaterial={handlePrintMaterial}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            {showCheatSheet && <CheatSheetSection />}
          </div>
          <div className={styles.action}>
            <Button
              size="small"
              className={styles.downButton}
              onClick={onClose}
              label={"Cancel"}
            />
            {hasLessonPresentationContents ? (
              <Button
                size="small"
                className={styles.startButton}
                onClick={onStartLesson}
                label={startLabel ? startLabel : "Begin Lesson"}
              />
            ) : completedPercent !== 100 ? (
              <Button
                size="small"
                className={styles.completeButton}
                onClick={onCompleteLesson}
                label={"Mark as Complete"}
              />
            ) : (
              <div className={styles.rateButton} onClick={openFeedback}>
                <LordIcon
                  src={IconRateLesson}
                  isHover={true}
                  colors={{ primary: "#6BC690", secondary: "#6BC690" }}
                  size={24}
                  target=".LessonProgressModal_feedbackButton__KoIAL"
                />
                <span>Rate Lesson</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default RequiredLessonMaterials;
